import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  useOutsideClick,
  Portal,
} from "@chakra-ui/react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { ReactComponent as InsightIcon } from "../../assets/svg/insight-icon.svg";
/**
 * ActivityCardDropdown - Context menu for activity cards
 * Shows options: Insight, Remix, Edit, Delete
 */
const ActivityCardDropdown = ({
  isOpen = false,
  onClose,
  onInsight,
  onRemix,
  onEdit,
  onDelete,
  position = { x: 0, y: 0 },
  anchorRef,
  activity, // Activity object to check for insight availability
  isActivityEditable = false, // Whether the activity can be edited
  ...props
}) => {
  const menuRef = React.useRef();

  // Close menu when clicking outside
  useOutsideClick({
    ref: menuRef,
    handler: onClose,
  });

  if (!isOpen) return null;

  const menuItems = [
    // Only include insight option if activity has insight
    ...(activity?.insight
      ? [
          {
            id: "insight",
            label: "Insight",
            icon: InsightIcon,
            onClick: onInsight,
            isIconComponent: true,
          },
        ]
      : []),
    {
      id: "remix",
      label: "Remix",
      icon: "🌀",
      onClick: onRemix,
      highlighted: true,
    },
    // Only include edit option if activity is not currently running/live
    ...(!isActivityEditable
      ? [
          {
            id: "edit",
            label: "Edit",
            icon: FiEdit,
            onClick: onEdit,
            isIconComponent: true,
            hasBorder: true,
          },
        ]
      : []),
    {
      id: "delete",
      label: "Delete",
      icon: FiTrash2,
      onClick: onDelete,
      isIconComponent: true,
      danger: true,
    },
  ];

  const handleItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    }
    onClose();
  };

  return (
    <Portal>
      <Box
        ref={menuRef}
        position="fixed"
        top={`${Math.max(
          10,
          Math.min(position.y, window.innerHeight - 200)
        )}px`}
        left={`${position.x}px`}
        zIndex={1000}
        bg="white"
        borderRadius="10px"
        border="1px solid"
        borderColor="rgba(208, 204, 213, 0.3)"
        boxShadow="0px 4px 8px rgba(208, 204, 213, 0.16), 0px 0px 4px rgba(208, 204, 213, 0.08)"
        py={0}
        w="133px"
        {...props}
      >
        <VStack spacing={0} align="stretch">
          {menuItems.map((item, index) => (
            <Box key={item.id}>
              <Box
                px="8px"
                py={item.highlighted ? "6px" : "10px"}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  bg: "#F6F5F7",
                }}
                onClick={() => handleItemClick(item)}
                data-testid={item.id === "remix" ? "remix-menu-option" : undefined}
              >
                <HStack spacing="8px" align="center" justify="space-between">
                  <Text
                    fontSize="12px"
                    fontWeight="400"
                    color={item.danger ? "#CC474E" : "#0C001C"}
                    lineHeight="1.5"
                    letterSpacing="-1%"
                    fontFamily="Geist, sans-serif"
                    flex="1"
                  >
                    {item.label}
                  </Text>

                  {item.isIconComponent ? (
                    <Box color={item.danger ? "#CC474E" : "#4C47CC"}>
                      {item.id === "insight" ? (
                        <item.icon width="16px" height="16px" />
                      ) : (
                        <item.icon size="16px" strokeWidth="1.25px" />
                      )}
                    </Box>
                  ) : item.icon ? (
                    <Text
                      fontSize="16px"
                      lineHeight="1.5"
                      fontFamily="Geist, sans-serif"
                    >
                      {item.icon}
                    </Text>
                  ) : null}
                </HStack>
              </Box>

              {/* Border separator after Edit item (before Delete) */}
              {item.hasBorder && <Box h="1px" bg="#F6F5F7" mx="8px" />}
            </Box>
          ))}
        </VStack>
      </Box>
    </Portal>
  );
};

export default ActivityCardDropdown;
